/**
 * A v4 UUID that works on a plain-HTTP LAN.
 *
 * `crypto.randomUUID()` is restricted to secure contexts — HTTPS or localhost.
 * Every captain tablet reaches this app at `http://192.168.x.x`, which is not
 * one, so the call was `undefined` there and threw inside POSProvider before
 * anything rendered. The result was a white screen on every device except the
 * server's own browser, which is the one place nobody takes an order from.
 *
 * `crypto.getRandomValues()` is NOT secure-context gated, so the fallback keeps
 * the same CSPRNG quality. That matters more than it looks: these ids become
 * `action_id` and `client_uuid`, which the backend uses to deduplicate replayed
 * mutations. A weak generator here would let two genuinely different orders
 * collide on one id, and the second would be silently discarded as a replay.
 *
 * There is deliberately no `Math.random` tier. It would turn a hard failure into
 * a rare, unattributable lost order.
 */
export function uuid() {
  const c = typeof crypto !== "undefined" ? crypto : undefined;

  if (typeof c?.randomUUID === "function") return c.randomUUID();

  if (typeof c?.getRandomValues === "function") {
    const bytes = c.getRandomValues(new Uint8Array(16));
    bytes[6] = (bytes[6] & 0x0f) | 0x40; // version 4
    bytes[8] = (bytes[8] & 0x3f) | 0x80; // variant 1 (RFC 4122)
    const hex = Array.from(bytes, b => b.toString(16).padStart(2, "0")).join("");
    return [
      hex.slice(0, 8),
      hex.slice(8, 12),
      hex.slice(12, 16),
      hex.slice(16, 20),
      hex.slice(20),
    ].join("-");
  }

  throw new Error(
    "No cryptographic random source available — cannot generate an id safely."
  );
}
